// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, ObservableInput, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import { TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { RootState } from "../../../app/store"
import { selectNarrateGptEnabled } from "../../user-context/slice"

type CreateTimeGroupRpc = (payload: Actions.CreateTimeGroupPayload) => Observable<TimeGroup>

export const createTimeGroup = (createTimeGroupRpc: CreateTimeGroupRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createTimeGroup.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(createTimeGroupRpc),
          withLatestFrom(state$),
          mergeMap(([timeGroupFromResponse, state]) => {
            const timeGroup = timeGroupFromResponse.toObject()
            const timeRowIdsList = action.payload.request.getTimeRowIdsList()
            const activeTimeRowIdsByDate = state.timeline.activeTimeRowIdsByDate[state.timeline.activeDate] ?? []
            const resetActiveTimeRowIdsByDate = timeRowIdsList.length !== 0
              && timeRowIdsList.every(id => activeTimeRowIdsByDate.includes(id))
            const actions: Action[] = [
              TimelineSlice.upsertTimeGroup(timeGroup),
              TimelineSlice.setTimeRowsGroupId({
                timeRowIds: timeRowIdsList,
                timeGroupId: timeGroup.id,
              }),
              TimelineSlice.addTimeGroupAwaitingSuggestions(timeGroup.id),
              Actions.generateTimesheetFieldConfigs([timeGroup]),
              TimelineSlice.addActiveTimeGroupId(timeGroup.id),
              TimelineSlice.removeTimeRowIdsInGroupingProgress(action.payload.request.getTimeRowIdsList())
            ]
            if (resetActiveTimeRowIdsByDate) {
              actions.unshift(TimelineSlice.setActiveTimeRowIds({ timeRowIds: []}))
            }
            if (action.payload.post) {
              actions.push(Actions.postTimeGroup(timeGroup))
            } else if (selectNarrateGptEnabled(state) && TimelineAdditionalUiSlice.selectAutoNarrative(state)) {
              actions.push(
                TimelineAdditionalUiSlice.addAutoNarrativeTimeGroupId(timeGroup.id)
              )
            }
            return concat(actions)
          }),
          catchError((e: unknown): ObservableInput<any> => {
            return concat(
              of(TimelineSlice.removeTimeRowIdsInGroupingProgress(action.payload.request.getTimeRowIdsList())),
              handleError(e),
            )
          }),
        )
      ),
    )

const createTimeGroupRpc = (payload: Actions.CreateTimeGroupPayload): Observable<TimeGroup> =>
  grpc(() => timelineClient.createTimeGroup(payload.request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_create_time_group_failed_precondition", message: "Failed to create time group. Time rows may be out of sync." }),
          messageType: "ERROR",
        })),
        of(Actions.onTimelineOutOfSync()),
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_create_time_group_permission_denied", message: "Failed to create time group. You do not have permission to group the selected time rows." }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_create_time_group_failed", message: "Failed to create the group. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default createTimeGroup(createTimeGroupRpc)
